import type { CSSProperties } from 'react';

export const SPARTAN_REVEAL_READY_CLASS = 'spartan-product-reveal-ready';

export const SPARTAN_REVEAL_PRESET = {
  standard: 'standard',
  content: 'content',
  collection: 'collection',
  media: 'media',
  gallery: 'gallery',
  galleryThumbnails: 'gallery-thumbnails',
} as const;

export const SPARTAN_REVEAL_ROLE = {
  standard: 'standard',
  fade: 'fade',
  copy: 'copy',
  exploreCard: 'explore-card',
  horizontalCard: 'horizontal-card',
  lineupCard: 'lineup-card',
  lineupImage: 'lineup-image',
  galleryCopy: 'gallery-copy',
  galleryMain: 'gallery-main',
  galleryThumbnail: 'gallery-thumbnail',
} as const;

type SpartanRevealPreset =
  (typeof SPARTAN_REVEAL_PRESET)[keyof typeof SPARTAN_REVEAL_PRESET];
type SpartanRevealRole =
  (typeof SPARTAN_REVEAL_ROLE)[keyof typeof SPARTAN_REVEAL_ROLE];

interface SpartanRevealStyle extends CSSProperties {
  '--spartan-reveal-delay'?: string;
  '--spartan-reveal-duration'?: string;
  '--spartan-reveal-order'?: number;
}

interface SpartanRevealGroupOptions {
  isSelf?: boolean;
  scrollOffset?: string;
}

interface SpartanRevealItemOptions {
  maxDelayMs?: number;
  order?: number;
  role?: SpartanRevealRole;
  staggerMs?: number;
  style?: SpartanRevealStyle;
}

interface SpartanRevealElementOptions extends SpartanRevealItemOptions {
  preset?: SpartanRevealPreset;
  scrollOffset?: string;
}

export const SPARTAN_REVEAL_DEFAULT_STAGGER_MS = 80;
export const SPARTAN_REVEAL_MAX_DELAY_MS = 320;
export const SPARTAN_REVEAL_IMMEDIATE_SCROLL_OFFSET = '0%,0';

export const SPARTAN_REVEAL_SCROLL_OFFSET = {
  [SPARTAN_REVEAL_PRESET.standard]: '20%,0',
  [SPARTAN_REVEAL_PRESET.content]: '22%,0',
  [SPARTAN_REVEAL_PRESET.collection]: '28%,0',
  [SPARTAN_REVEAL_PRESET.media]: '24%,0',
  [SPARTAN_REVEAL_PRESET.gallery]: '24%,0',
  [SPARTAN_REVEAL_PRESET.galleryThumbnails]: '14%,0',
} as const satisfies Record<SpartanRevealPreset, string>;

export const getSpartanRevealGroupProps = (
  preset: SpartanRevealPreset = SPARTAN_REVEAL_PRESET.standard,
  options: SpartanRevealGroupOptions = {}
) => {
  const scrollOffset = options.scrollOffset;

  return {
    ...(scrollOffset ? { 'data-spartan-reveal-offset': scrollOffset } : {}),
    'data-spartan-reveal': '',
    'data-spartan-reveal-preset': preset,
    ...(options.isSelf ? { 'data-spartan-reveal-self': '' } : {}),
  };
};

export const getSpartanRevealItemProps = (
  options: SpartanRevealItemOptions = {}
) => {
  const {
    maxDelayMs = SPARTAN_REVEAL_MAX_DELAY_MS,
    order = 0,
    role = SPARTAN_REVEAL_ROLE.standard,
    staggerMs = SPARTAN_REVEAL_DEFAULT_STAGGER_MS,
    style: itemStyle,
  } = options;
  const delayMs = Math.min(
    Math.max(0, order) * Math.max(0, staggerMs),
    Math.max(0, maxDelayMs)
  );
  const style: SpartanRevealStyle = {
    ...itemStyle,
    '--spartan-reveal-delay': `${delayMs}ms`,
    '--spartan-reveal-order': order,
  };

  return {
    'data-spartan-reveal-item': '',
    'data-spartan-reveal-role': role,
    style,
  };
};

export const getSpartanRevealElementProps = (
  options: SpartanRevealElementOptions = {}
) => {
  const {
    preset = SPARTAN_REVEAL_PRESET.standard,
    scrollOffset,
    ...itemOptions
  } = options;

  return {
    ...getSpartanRevealGroupProps(preset, { isSelf: true, scrollOffset }),
    ...getSpartanRevealItemProps(itemOptions),
  };
};
